import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo, faSave } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { Controls, TypedFormGroup } from 'src/app/controls/forms/managed-form/ngx-sub-form-utils';
import { FormGroupOptions } from 'src/app/controls/forms/managed-form/ngx-sub-form.types';
import { DataInput } from 'src/app/controls/forms/management-form/form.decorators';
import { ManagementFormComponent } from 'src/app/controls/forms/management-form/management-form';
import { RichToolTipDirective } from 'src/app/directives/rich-tool-tip.directive';
import { EnumHelper } from 'src/app/helpers/enum-helper';
import { NumberKeyDictionary } from 'src/app/helpers/number-key-dictionary_T';
import { AttributeAssignmentRow } from 'src/app/models/attribute-assignment-row';
import { CriteriaSearchZone } from 'src/app/models/enums/criteria-search-zone';
import { ProcessType } from 'src/app/models/enums/process-type';
import { SearchMode } from 'src/app/models/enums/search-mode';
import { IProcessDefinition } from 'src/app/models/interfaces/process-definition.interface';
import { ProcessDefinition } from 'src/app/models/process-definition';
import { FormFocusDirective } from '../../../controls/directives/form-focus.directive';
import { MatFilterableSelectComponent } from '../../../controls/mat-filterable-select/mat-filterable-select.component';
import { SwapChipListsComponent } from '../../../controls/swap-chip-lists/swap-chip-lists.component';
import { ProcessDefinitionFormContext } from '../process-definition-form-context';
import { AttributeAssignmentTableComponent } from './attribute-assignment-table/attribute-assignment-table.component';
import { AutoLinkDefinitionComponent } from './auto-link/auto-link-definition.component';
import { AutoLinkFormData } from './auto-link/models/auto-link-form-data';

@Component({
    selector: 'app-process-definitions-form',
    templateUrl: './process-definitions-form.component.html',
    styleUrls: ['./process-definitions-form.component.scss'],
    imports: [
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        NgClass,
        MatFormFieldModule,
        MatInputModule,
        FaIconComponent,
        MatTooltipModule,
        MatSelectModule,
        MatOptionModule,
        SwapChipListsComponent,
        MatCheckboxModule,
        MatFilterableSelectComponent,
        AttributeAssignmentTableComponent,
        AutoLinkDefinitionComponent,
        MatButtonModule,
        RichToolTipDirective,
    ]
})
export class ProcessDefinitionsFormComponent extends ManagementFormComponent<IProcessDefinition> implements OnInit, OnChanges {
    public faSave = faSave;
    public faCircleInfo = faCircleInfo;

    /**
     * Mode d'affichage du formulaire (consultation / edition / création)
     */
    public FormMode = FormMode;

    public ProcessType = ProcessType;

    public SearchMode = SearchMode;

    public processTypes = [];

    public searchModes = [];

    public nameTooltipContent = "Nom du processus. Ce nom sera affiché dans les listes déroulantes de selection d'un processus.";

    public typeTooltipContent =
        'Ce paramètre permet de spécifier le type de processus.<br><ul><li><b>Identification du client</b>: Ce processus permet de déterminer à partir du mail reçu, le client à l’origine du mail. </li><li><b>Identification du type de document</b>:  Ce processus permet de déterminer à partir du mail reçu, le type de document contenu par le mail. </li><li><b>Assignation d’attributs</b>:  Ce processus permet d’alimenter des attributs du dossier avec des valeurs fixes ou des valeurs récupérées dans le mail. </li><li><b>Rattachement automatique</b>:  Ce processus permet de rattacher le mail reçu à un dossier existant.</li></ul>';

    public customerIdTooltipContent =
        "Ce paramètre permet de spécifier le client pour lequel ce processus s'applique.<br>Par défaut, le processus s'applique à tous les mails reçus sur le canal. Si un client est spécifié, le processus ne sera appliqué que pour les mails reçus depuis ce client.";

    public documentTypeIdTooltipContent =
        "Ce paramètre permet de spécifier le type de document pour lequel ce processus s'applique.<br>Par défaut, le processus s'applique à tous les mails reçus sur le canal. Si un type de document est spécifié, le processus ne sera appliqué que pour les mails contenant ce type de client.";

    public issuerTooltipContent =
        "Ce paramètre permet de spécifier l'émetteur pour lequel ce processus s'applique.<br>Par défaut, le processus s'applique à tous les mails reçus sur le canal. Si un emetteur est spécifié, le processus ne sera appliqué que pour les mails envoyés par cet émetteur.";

    public searchModeTooltipContentAttributesAssignment =
        'Ce paramètre permet de définir le mode de recherche : Indique dans quel cas les attributs spécifiés dans <b>Attributs à renseigner</b> sont renseignés<ul><li><b>Aucun</b> : Les attributs sont systématiquement renseignés avec la valeur spécifiée.</li><li><b>Par mots clés</b> : les attributs sont renseignés si l’expression booléenne spécifiée dans ‘Expression de recherche’ est vraie.</li><li><b>Par expression régulière</b> : les attributs sont renseignés si l’expression régulière est vérifiée.</li></ul>';

    public searchModeTooltipContentCustomerIdentification =
        'Ce paramètre permet de définir le mode de recherche : Indique dans quel cas le client spécifié dans le champ <b>client reconnu</b> est utilisé pour identifier le client.<ul><li><b>Aucun</b> : Le client spécifié est systématiquement utilisé pour identifier le client. </li><li><b>Par mots clés</b> : Le client spécifié est utilisé pour identifier le client si l’expression booléenne spécifiée dans ‘Expression de recherche’ est vraie. </li><li><b>Par expression régulière</b> : Le client spécifié est utilisé pour identifier le client si l’expression régulière est vérifiée. </li></ul>';

    public searchModeTooltipContentDocumentTypeIdentification =
        'Ce paramètre permet de définir le mode de recherche : Indique dans quel cas le type de document spécifié dans le champ <b>type de document reconnu</b> est utilisé pour identifier le type de document.<ul><li><b>Aucun</b> : Le type de document spécifié est systématiquement utilisé pour identifier le type de document. </li><li><b>Par mots clés</b> : Le type de document spécifié est utilisé pour identifier le type de document si l’expression booléenne spécifiée dans ‘Expression de recherche’ est vraie. </li><li><b>Par expression régulière</b> : Le type de document spécifié est utilisé pour identifier le type de document si l’expression régulière est vérifiée. </li></ul>';

    public searchModeTooltipContentAutoLink =
        'Ce paramètre permet de définir le mode de recherche : Indique dans quel cas le rattachement automatique spécifié est appliqué.<ul><li><b>Aucun</b> : Le rattachement automatique spécifié est systématiquement appliqué. </li><li><b>Par mots clés</b> : Le rattachement automatique spécifié est appliqué si l’expression booléenne spécifiée dans ‘Expression de recherche’ est vraie. </li><li><b>Par expression régulière</b> : Le rattachement automatique spécifié est appliqué si l’expression régulière est vérifiée. </li></ul>';

    public searchExpressionTooltipContent =
        'Ce paramètre permet de définir l’expression à rechercher dans les zones du mail :<br>Dans le cas d’une expression booléenne de mots clés, le système va évaluer l’expression en vérifiant la présence ou l’absence de mots clés spécifiés dans l’expression. Les opérateurs possibles sont :<br><ul><li><b>!</b>  : Opérateur de négation. Un mot clé préfixé par cet opérateur indiquera que le texte évalué ne devra pas contenir ce mot clé.</li><li><b>||</b>  : Opérateur OU. La présence de cet opérateur entre 2 mots clés indiquera que le texte évalué devra contenir au moins un des 2 mots clés. </li><li><b>&&</b>  : Opérateur ET. La présence de cet opérateur entre 2 mots clés indiquera que le texte évalué devra contenir les 2 mots clés. </li><li><b>^</b> : Opérateur OU exclusif. La présence de cet opérateur entre 2 mots clés indiquera que le texte évalué devra contenir un seul des 2 mots clés. </li></ul><br>A noter qu’il est également possible d’utiliser des parenthèses dans l’expression booléenne.<br><br>Dans le cas d’une expression régulière, le système vérifie si l’expression spécifiée trouve une correspondance dans le texte.<br>A noter que dans ce mode, il est possible de capturer une valeur avec l’expression régulière pour l’utiliser comme valeur d’un attribut. Le nom du groupe de capture devra être spécifié comme valeur de l’attribut<br>Par exemple pour l’expression régulière suivante : <br><code>Date RDV : (?&lt;value&gt;\\d{2}\\/\\d{2}\\/\\d{4} \\d{2}:\\d{2}:\\d{2})</code><br>Le groupe de capture étant nommé value, pour assigner le texte capturé par l\'expression régulière à l\'attribut, il faudra specifier <b>&lt;value&gt;</b> comme valeur de l\'attribut<br>Pour connaitre la syntaxe des expressions régulières et pouvoir les tester, vous pouvez utiliser le site suivant : <a href="https://regex101.com/" target="_blank">https://regex101.com/</a>';

    public wholeWordOnlyEnabledTooltipContent =
        'Si cette option est activée, seules des correspondances sur des mots entiers sont valides.<br>Si le mot recherché est contenu dans un mot du texte recherché alors la correspondance sera ignorée (ex : commande et recommander)';

    public sensitiveCaseEnabledTooltipContent =
        "Par défaut la recherche est insensible à la casse.<br>C'est à dire que la recherche ne fait pas de distinction entre majuscule et minuscule.<br>Si cette option est activée, la recherche respecte la présence de majuscule ou minuscule.";

    public specialCaractersIgnoredTooltipContent =
        'Par défaut la recherche fait la distinction entre une lettre sans accent et la même lettre accentuée, idem pour les cédilles et les tildes.<br>Si cette option est activée, une lettre sans accent et la même lettre accentuée seront considérées comme identiques, idem pour les cédilles et les tildes.';

    public minorDifferencesAcceptedTooltipContent = "Si cette option est activée, des différences minimes comme l'absence ou l'ajout d'une lettre seront ignorées.";

    public pluralizeEnabledTooltipContent =
        'Si cette option est activée, la recherche se fera aussi bien sur le mot au sigulier que sur le mot au pluriel (exemple de format : commande(s), chev(al/aux)).';

    public identifiedCustomerTooltipContent =
        "Ce paramètre permet d'indiquer à quel client le document reçu correspond. Le client spécifié sera stocké dans l'attribut client du dossier.<br>Ce client peut également être utilisé par d'autres processus éxécutés postérieurement.";

    public identifiedDocumentTypeTooltipContent =
        "Ce paramètre permet d'indiquer à quel type de document le document reçu correspond.<br>Le type de document peut être utilisé par d'autres processus éxécutés postérieurement.";
    /**
     * Infos de la définition de processus à afficher dans le formulaire.
     */
    @DataInput()
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('processDefinition')
    public dataInput: Required<IProcessDefinition> | null | undefined;

    /**
     * Contexte du formulaire (mode d'affichage ...).
     */
    public _context: ProcessDefinitionFormContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<ProcessDefinitionFormContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: ProcessDefinitionFormContext) {
        this._context$.next(value);
    }

    /*
     * Indique si le composant est en mode debug (affiche des informations pour le developpeur)
     */
    public debugMode = false;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('processDefinitionUpdated')
    public dataOutput: EventEmitter<IProcessDefinition> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    constructor(public enumHelper: EnumHelper) {
        super();
    }

    /**
     * Initialise le composant.
     */
    override ngOnInit() {
        super.ngOnInit();

        // S'abonne aux changements de contexte
        this._context$.subscribe((x) => {
            this._context = x;
            this.dataInput$.next(x.item);

            this.processTypes = [];
            this.processTypes.push({
                value: ProcessType.CustomerIdentification,
                text: 'Identification du client',
            });
            this.processTypes.push({
                value: ProcessType.DocumentTypeIdentification,
                text: 'Identification du type de document',
            });
            this.processTypes.push({
                value: ProcessType.AttributesAssignment,
                text: "Assignation d'attributs",
            });
            this.processTypes.push({
                value: ProcessType.AutoLink,
                text: 'Rattachement automatique',
            });

            this.resetSearchModes();
        });
    }

    private resetSearchModes() {
        console.log('resetSearchModes');
        this.searchModes = [];
        this.searchModes.push({ value: SearchMode.None, text: 'Aucun' });

        if (this._context.item.type !== ProcessType.AutoLink) {
            this.searchModes.push({
                value: SearchMode.KeyWord,
                text: 'Par mots clés',
            });
        }

        this.searchModes.push({
            value: SearchMode.RegEx,
            text: 'Par expression régulière',
        });
        this.formGroup.get('searchExpression').updateValueAndValidity();

        console.log('resetSearchModes :', this.searchModes);
    }

    /**
     * Utilisé pour rendre le champ id read-only
     */
    ngOnChanges() {
        this.formGroup.get('id').disable();
    }

    /**
     * Retourne la liste des contrôles du formulaire
     */
    protected getFormControls(): Controls<IProcessDefinition> {
        return {
            id: new UntypedFormControl(null, Validators.required),
            type: new UntypedFormControl(null, Validators.required),
            name: new UntypedFormControl(null, Validators.required),
            definitionJson: new UntypedFormControl(null),
            issuer: new UntypedFormControl(null),
            searchMode: new UntypedFormControl(null, Validators.required),
            searchExpression: new UntypedFormControl(null),
            wholeWordOnlyEnabled: new UntypedFormControl(null),
            sensitiveCaseEnabled: new UntypedFormControl(null),
            specialCaractersIgnored: new UntypedFormControl(null),
            minorDifferencesAccepted: new UntypedFormControl(null),
            pluralizeEnabled: new UntypedFormControl(null),
            customerId: new UntypedFormControl(null),
            documentTypeId: new UntypedFormControl(null),
            identifiedCustomerId: new UntypedFormControl(null),
            identifiedDocumentTypeId: new UntypedFormControl(null),
            criteriaSearchZones: new UntypedFormControl(null),
            attributesAssignment: new UntypedFormControl(null),
            autoLinkDatas: new UntypedFormControl(null),
        };
    }

    protected override getFormGroupControlOptions(): FormGroupOptions<IProcessDefinition> {
        return {
            validators: [
                this.conditionalRequired('searchExpression', 'searchMode', SearchMode.KeyWord),
                this.conditionalRequired('searchExpression', 'searchMode', SearchMode.RegEx),
                this.conditionalRequired('criteriaSearchZones', 'searchMode', SearchMode.KeyWord),
                this.conditionalRequired('criteriaSearchZones', 'searchMode', SearchMode.RegEx),
                this.conditionalRequired('identifiedCustomerId', 'type', ProcessType.CustomerIdentification),
                this.conditionalRequired('identifiedDocumentTypeId', 'type', ProcessType.DocumentTypeIdentification),
                this.conditionalRequired('attributesAssignment', 'type', ProcessType.AttributesAssignment),
            ],
        };
    }

    /**
     *  Appellé lorsque l'utilisateur clique sur le bouton 'sauver'
     */
    public override manualSave(): void {
        console.log('ProcessDefinitionComponent manualSave', this.dataValue);
        let updatedValue = ProcessDefinition.createFromForm(this.dataValue);
        console.log('ProcessDefinitionComponent manualSave after transformation', updatedValue);
        this.dataValue = updatedValue;
        super.manualSave();
    }

    oncriteriaSearchZonesChange(zones: CriteriaSearchZone[]) {
        console.log('CriteriaSearchZones', zones);
    }

    onTypeChanged(event: MatSelectChange) {
        console.log('Type has changed', event);
        this._context.item.type = event.value;

        this.formGroup.get('identifiedCustomerId').updateValueAndValidity();
        this.formGroup.get('identifiedDocumentTypeId').updateValueAndValidity();
        this.formGroup.get('attributesAssignment').updateValueAndValidity();
        this.resetSearchModes();
    }

    onSearchModeChanged(event: MatSelectChange) {
        console.log('Search mode has changed', event);
        this._context.item.searchMode = event.value;
        this.formGroup.get('searchExpression').updateValueAndValidity();
        this.formGroup.get('criteriaSearchZones').updateValueAndValidity();
    }

    onAttributesChanged(attributes: AttributeAssignmentRow[]) {
        this.formGroup.get('attributesAssignment').updateValueAndValidity();
    }

    onAutoLinkDatasChanged(autoLinkDatas: AutoLinkFormData) {
        this.formGroup.get('autoLinkDatas').updateValueAndValidity();
    }

    conditionalRequired(controlName: string, conditionalControlName: string, conditionalControlValue: any): ValidatorFn {
        return (formGroup: TypedFormGroup<IProcessDefinition>): ValidationErrors | null => {
            const control = formGroup.controls[controlName];
            const conditionalControl = formGroup.controls[conditionalControlName];

            if (control.errors && !control.errors.mustMatch) {
                // return if another validator has already found an error on the matchingControl
                return null;
            }

            // set error on Control if validation fails
            if (
                conditionalControl.value === conditionalControlValue &&
                ((Array.isArray(control.value) && control.value.length === 0) ||
                    (control.value instanceof NumberKeyDictionary && (control.value as NumberKeyDictionary<string>).isEmpty()) ||
                    (!Array.isArray(control.value) && !control.value))
            ) {
                control.setErrors({ required: true });
                return { required: true };
            } else {
                control.setErrors(null);
                return null;
            }
        };
    }

    public cancel(): void {
        this.canceled.emit(true);
    }
}
